package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.shared.animation.AnimationSetup
import dev.danko.roblox_craft_ai.shared.animation.CombatMode
import dev.danko.roblox_craft_ai.shared.animation.WeaponKind
import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.shared.animation.R15Skeleton
import dev.danko.roblox_craft_ai.shared.animation.R15Fingers
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.i18n.MeshViewerStrings
import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.MutableState
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.shared.model.AssetType
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import dev.danko.roblox_craft_ai.uikit.util.EasingStyle
import dev.danko.roblox_craft_ai.uikit.util.Keyframe
import dev.danko.roblox_craft_ai.uikit.util.KeyframeSequence
import dev.danko.roblox_craft_ai.uikit.util.PoseDto
import dev.danko.roblox_craft_ai.uikit.util.applyEasing
import dev.danko.roblox_craft_ai.uikit.util.findSurroundingKeyframes
import dev.danko.roblox_craft_ai.uikit.util.parseRobloxKeyframeSequenceJson
import dev.danko.roblox_craft_ai.uikit.util.wrapTime
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import kotlin.math.cos
import kotlin.math.floor
import kotlin.math.sin

val MeshViewerContainerStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(colorMode.toAppPalette().surface)
        .borderRadius(0.5.cssRem)
        .border(1.px, LineStyle.Solid, colorMode.toAppPalette().border)
        .overflow(Overflow.Hidden)
        .position(Position.Relative)
}

val MeshControlsOverlayStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .padding(0.25.cssRem)
        .backgroundColor(palette.surfaceElevated.toRgb().copy(alpha = 230))
        .borderRadius(0.375.cssRem)
        .border(1.px, LineStyle.Solid, palette.border)
}

val TimelineSurfaceStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .fillMaxWidth()
        .padding(0.75.cssRem)
        .backgroundColor(palette.surfaceElevated)
        .borderTop(1.px, LineStyle.Solid, palette.border)
}

val ScrubberContainerStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .height(20.px)
        .position(Position.Relative)
        .margin(top = 0.5.cssRem, bottom = 0.25.cssRem)
}

enum class MeshShadingMode(val title: String, val icon: String) {
    TEXTURED("Texture", "🎨"),
    CLAY("Clay", "🧱"),
    WIREFRAME_OVERLAY("Wire", "🕸️"),
    FLAT("Flat", "💎"),
    BASE_COLOR("Colour", "🖍️"),
    NORMALS("Normals", "🌈"),
    UV_CHECKER("UV", "🏁"),
    XRAY("X-ray", "👻")
}

enum class MeshLightingPreset(val title: String, val icon: String) {
    STUDIO("Studio", "💡"),
    ROBLOX("Roblox", "🟦"),
    SUNLIGHT("Sun", "☀️"),
    NIGHT("Night", "🌙"),
    CYBERPUNK("Neon", "🟣")
}

/** Roblox models and characters face -Z, so "front" looks from -Z (rotY = PI) and "side" from the right (+X). */
enum class CameraViewPreset(val label: String, val rotX: Double, val rotY: Double) {
    RESET("Iso", 0.38, 2.42),
    FRONT("Front", 0.0, 3.14159),
    TOP("Top", 1.55, 3.14159),
    RIGHT("Side", 0.0, 1.57079)
}

internal fun MeshShadingMode.localizedTitle(s: MeshViewerStrings): String = when (this) {
    MeshShadingMode.TEXTURED -> s.shadingTextured
    MeshShadingMode.CLAY -> s.shadingClay
    MeshShadingMode.WIREFRAME_OVERLAY -> s.shadingWire
    MeshShadingMode.FLAT -> s.shadingFlat
    MeshShadingMode.BASE_COLOR -> s.shadingBaseColor
    MeshShadingMode.NORMALS -> s.shadingNormals
    MeshShadingMode.UV_CHECKER -> s.shadingUvChecker
    MeshShadingMode.XRAY -> s.shadingXray
}

internal fun MeshLightingPreset.localizedTitle(s: MeshViewerStrings): String = when (this) {
    MeshLightingPreset.STUDIO -> s.lightStudio
    MeshLightingPreset.ROBLOX -> s.lightRoblox
    MeshLightingPreset.SUNLIGHT -> s.lightSun
    MeshLightingPreset.NIGHT -> s.lightNight
    MeshLightingPreset.CYBERPUNK -> s.lightNeon
}

private fun CameraViewPreset.localizedLabel(s: MeshViewerStrings): String = when (this) {
    CameraViewPreset.RESET -> s.cameraIso
    CameraViewPreset.FRONT -> s.cameraFront
    CameraViewPreset.TOP -> s.cameraTop
    CameraViewPreset.RIGHT -> s.cameraSide
}

data class MeshModelStats(
    val triangles: Int = 0,
    val vertices: Int = 0,
    val meshCount: Int = 0,
    val dimensionsStuds: String = "0 × 0 × 0",
    /** X, Y, Z in studs (1 glTF unit = 1 stud), measured before the viewer scales the model to fit. */
    val sizeStuds: List<Double> = emptyList(),
    /** Largest side of the biggest texture in pixels; 0 = no texture. */
    val textureSize: Int = 0,
    val hasNormalMap: Boolean = false,
    val loaded: Boolean = false
)

/** Loads three.js and its GLTF loader once, then calls [onLoaded] when both are ready (never before the loader). */
fun ensureThreeJsLoaded(onLoaded: () -> Unit = {}, attempt: Int = 0) {
    val threeLoaded = js("typeof window.THREE !== 'undefined'") as Boolean
    val gltfLoaded = threeLoaded && js("typeof window.THREE.GLTFLoader !== 'undefined'") as Boolean
    if (gltfLoaded) {
        onLoaded()
        return
    }
    if (!threeLoaded && document.querySelector("script[src*='three.min.js'], script[src*='three.js']") == null) {
        val script = document.createElement("script")
        script.setAttribute("src", "https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js")
        document.head?.appendChild(script)
    }
    // The loader extends THREE, so it can only be added once three.js itself has run.
    if (threeLoaded && js("window.__robloxGLTFLoaderStarted !== true") as Boolean) {
        js("window.__robloxGLTFLoaderStarted = true")
        val loaderScript = document.createElement("script")
        loaderScript.setAttribute("src", "https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js")
        loaderScript.setAttribute("crossorigin", "anonymous")
        document.head?.appendChild(loaderScript)
    }
    if (attempt > 150) return
    window.setTimeout({ ensureThreeJsLoaded(onLoaded, attempt + 1) }, 100)
}

private const val DEFAULT_ROT_X = 0.38
private const val DEFAULT_ROT_Y = 2.42
private const val DEFAULT_ZOOM = 6.2
private const val ANIMATION_ZOOM = 11.0
private const val DRAG_SENSITIVITY = 0.005
private const val PAN_SENSITIVITY = 0.002
private const val ZOOM_MIN = 0.4
private const val ZOOM_MAX = 35.0
private const val ROT_X_MIN = -1.55
private const val ROT_X_MAX = 1.55
private const val ZOOM_WHEEL_FACTOR = 0.001
/** Length of the spin video: one full turn. */
private const val VIDEO_MS = 6000
/** Half the width of the reference avatar across the arms, in studs. */
private const val AVATAR_HALF_WIDTH = 1.95

fun applyShadingModeToMesh(root: dynamic, mode: MeshShadingMode) {
    if (root == null || root == undefined) return
    MeshSceneJs.shade(root, mode.name)
}

fun calculateStatsForObject(root: dynamic): MeshModelStats {
    if (root == null || root == undefined) return MeshModelStats()
    val statsObj: dynamic = js("""
        (function(obj) {
            var THREE = window.THREE;
            if (!THREE || !obj) return { tri: 0, vert: 0, mesh: 0, dims: "0 × 0 × 0", sx: 0, sy: 0, sz: 0 };
            var triCount = 0;
            var vertCount = 0;
            var mCount = 0;
            var box = new THREE.Box3();
            obj.traverse(function(c) {
                if (c && c.isMesh && c.geometry) {
                    mCount++;
                    var geo = c.geometry;
                    if (geo.index) {
                        triCount += Math.floor(geo.index.count / 3);
                    } else if (geo.attributes && geo.attributes.position) {
                        triCount += Math.floor(geo.attributes.position.count / 3);
                    }
                    if (geo.attributes && geo.attributes.position) {
                        vertCount += geo.attributes.position.count;
                    }
                    box.expandByObject(c);
                }
            });
            var size = box.getSize(new THREE.Vector3());
            var dims = size.x.toFixed(1) + ' × ' + size.y.toFixed(1) + ' × ' + size.z.toFixed(1) + ' studs';
            return { tri: triCount, vert: vertCount, mesh: mCount, dims: dims, sx: size.x, sy: size.y, sz: size.z };
        })(root);
    """)
    val tri = statsObj.tri as? Int ?: 0
    val vert = statsObj.vert as? Int ?: 0
    val mesh = statsObj.mesh as? Int ?: 0
    val dims = statsObj.dims as? String ?: "0 × 0 × 0"
    return MeshModelStats(
        triangles = tri,
        vertices = vert,
        meshCount = mesh,
        dimensionsStuds = dims,
        sizeStuds = listOf(statsObj.sx as Double, statsObj.sy as Double, statsObj.sz as Double),
        textureSize = (MeshSceneJs.textureSize(root) as? Int) ?: 0,
        hasNormalMap = MeshSceneJs.relief(root, true) == true,
        loaded = true
    )
}

fun takeMeshScreenshot(renderer: dynamic, scene: dynamic, camera: dynamic, filename: String = "roblox_model_preview.png") {
    if (renderer == null || scene == null || camera == null) return
    try {
        renderer.render(scene, camera)
        val canvas = renderer.domElement as? HTMLCanvasElement ?: return
        val dataUrl = canvas.toDataURL("image/png")
        val a = document.createElement("a")
        a.setAttribute("href", dataUrl)
        a.setAttribute("download", filename)
        document.body?.appendChild(a)
        a.asDynamic().click()
        document.body?.removeChild(a)
        try {
            val viewerStrings = LocaleController.strings.meshViewer
            ToastManager.success(viewerStrings.screenshotSaved(filename), title = viewerStrings.screenshotTitle)
        } catch (_: Throwable) {}
    } catch (e: Throwable) {
        js("console.error('[MeshViewer] takeScreenshot error', e)")
    }
}

fun buildProceduralMesh(prompt: String): dynamic {
    return js("""
        (function(pr) {
            var THREE = window.THREE;
            if (!THREE) return null;
            var group = new THREE.Group();
            var p = (pr || "").toLowerCase();

            var isSword = p.indexOf("меч") !== -1 || p.indexOf("sword") !== -1 || p.indexOf("клинок") !== -1 || p.indexOf("blade") !== -1 || p.indexOf("weapon") !== -1 || p.indexOf("оружи") !== -1;
            var isWood = p.indexOf("дерев") !== -1 || p.indexOf("wood") !== -1 || p.indexOf("брус") !== -1;
            var isAxe = p.indexOf("топор") !== -1 || p.indexOf("axe") !== -1 || p.indexOf("секира") !== -1;
            var isShield = p.indexOf("щит") !== -1 || p.indexOf("shield") !== -1;
            var isChest = p.indexOf("сундук") !== -1 || p.indexOf("chest") !== -1 || p.indexOf("ящик") !== -1 || p.indexOf("box") !== -1;
            var isHouse = p.indexOf("дом") !== -1 || p.indexOf("house") !== -1 || p.indexOf("здание") !== -1 || p.indexOf("building") !== -1 || p.indexOf("замок") !== -1;
            var isTree = p.indexOf("дерево") !== -1 || p.indexOf("tree") !== -1 || p.indexOf("растение") !== -1 || p.indexOf("куст") !== -1;
            var isCar = p.indexOf("машина") !== -1 || p.indexOf("car") !== -1 || p.indexOf("авто") !== -1 || p.indexOf("vehicle") !== -1;
            var isGun = p.indexOf("пистолет") !== -1 || p.indexOf("gun") !== -1 || p.indexOf("бластер") !== -1 || p.indexOf("rifle") !== -1 || p.indexOf("винтовка") !== -1;

            var isAnime = p.indexOf("аниме") !== -1 || p.indexOf("anime") !== -1 || p.indexOf("манга") !== -1 || p.indexOf("manga") !== -1 || p.indexOf("фэнтези") !== -1 || p.indexOf("fantasy") !== -1 || p.indexOf("магич") !== -1;
            var hasSapphire = p.indexOf("сапфир") !== -1 || p.indexOf("sapphire") !== -1 || p.indexOf("лазурит") !== -1 || p.indexOf("синий камень") !== -1 || p.indexOf("blue gem") !== -1;
            var hasRuby = p.indexOf("рубин") !== -1 || p.indexOf("ruby") !== -1 || p.indexOf("красный камень") !== -1 || p.indexOf("red gem") !== -1;
            var hasEmerald = p.indexOf("изумруд") !== -1 || p.indexOf("emerald") !== -1 || p.indexOf("зеленый камень") !== -1;
            var hasAmethyst = p.indexOf("аметист") !== -1 || p.indexOf("amethyst") !== -1 || p.indexOf("фиолетов") !== -1;
            var hasDiamond = p.indexOf("алмаз") !== -1 || p.indexOf("бриллиант") !== -1 || p.indexOf("diamond") !== -1;
            var hasGem = hasSapphire || hasRuby || hasEmerald || hasAmethyst || hasDiamond || p.indexOf("кристалл") !== -1 || p.indexOf("gem") !== -1 || p.indexOf("crystal") !== -1 || p.indexOf("камен") !== -1;
            var hasRunes = p.indexOf("рун") !== -1 || p.indexOf("rune") !== -1 || p.indexOf("глиф") !== -1 || p.indexOf("glyph") !== -1 || p.indexOf("иероглиф") !== -1 || p.indexOf("свечен") !== -1 || p.indexOf("glow") !== -1 || p.indexOf("magic") !== -1;

            if (isSword) {
                // Gem & Rune Color Palettes
                var gemColor = 0x1D4ED8;
                var gemEmissive = 0x3B82F6;
                var runeColor = 0x38BDF8;
                var runeEmissive = 0x67E8F9;

                if (hasRuby) {
                    gemColor = 0xB91C1C;
                    gemEmissive = 0xEF4444;
                    runeColor = 0xFCA5A5;
                    runeEmissive = 0xF87171;
                } else if (hasEmerald) {
                    gemColor = 0x047857;
                    gemEmissive = 0x10B981;
                    runeColor = 0x6EE7B7;
                    runeEmissive = 0x34D399;
                } else if (hasAmethyst) {
                    gemColor = 0x7E22CE;
                    gemEmissive = 0xA855F7;
                    runeColor = 0xD8B4FE;
                    runeEmissive = 0xC084FC;
                } else if (hasDiamond) {
                    gemColor = 0xE0F2FE;
                    gemEmissive = 0xBAE6FD;
                    runeColor = 0xF0F9FF;
                    runeEmissive = 0xE0F2FE;
                }

                // Blade Materials
                var bladeColor = isWood ? 0x9E6B38 : (isAnime ? 0xE2E8F0 : 0xCBD5E1);
                var bladeRough = isWood ? 0.65 : (isAnime ? 0.15 : 0.25);
                var bladeMetal = isWood ? 0.0 : (isAnime ? 0.85 : 0.6);
                var guardColor = isWood ? 0x4A2511 : (isAnime ? 0xF59E0B : 0xD4AF37);
                var gripColor = isWood ? 0x2B170C : (isAnime ? 0x0F172A : 0x1A1A1A);
                var fullerColor = isWood ? 0x6E431F : (hasRunes ? 0x0F172A : (isAnime ? 0x1E293B : 0x94A3B8));

                var bladeMat = new THREE.MeshStandardMaterial({ color: bladeColor, roughness: bladeRough, metalness: bladeMetal });
                var fullerMat = new THREE.MeshStandardMaterial({
                    color: fullerColor,
                    roughness: hasRunes ? 0.3 : 0.8,
                    metalness: hasRunes ? 0.9 : 0.0
                });
                var guardMat = new THREE.MeshStandardMaterial({
                    color: guardColor,
                    roughness: 0.3,
                    metalness: isWood ? 0.05 : 0.85
                });
                var darkAccentMat = new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.4, metalness: 0.7 });

                // 1. Main Blade
                var bladeWidth = isAnime ? 0.42 : 0.38;
                var bladeHeight = isAnime ? 2.5 : 2.3;
                var bladeDepth = isAnime ? 0.065 : 0.08;
                var bladeGeo = new THREE.BoxGeometry(bladeWidth, bladeHeight, bladeDepth);
                var blade = new THREE.Mesh(bladeGeo, bladeMat);
                blade.position.y = isAnime ? 0.85 : 0.8;
                group.add(blade);

                // Anime Side Edges (High contrast beveled razor edge)
                if (isAnime) {
                    var edgeGeo = new THREE.BoxGeometry(0.05, bladeHeight - 0.04, 0.035);
                    var edgeLeft = new THREE.Mesh(edgeGeo, bladeMat);
                    edgeLeft.position.set(-(bladeWidth / 2 + 0.02), blade.position.y, 0);
                    group.add(edgeLeft);
                    var edgeRight = new THREE.Mesh(edgeGeo, bladeMat);
                    edgeRight.position.set((bladeWidth / 2 + 0.02), blade.position.y, 0);
                    group.add(edgeRight);
                }

                // 2. Blade Central Fuller Channel
                var fullerWidth = hasRunes ? 0.15 : 0.12;
                var fullerHeight = isAnime ? 2.2 : 2.0;
                var fullerDepth = isAnime ? 0.075 : 0.09;
                var fullerGeo = new THREE.BoxGeometry(fullerWidth, fullerHeight, fullerDepth);
                var fuller = new THREE.Mesh(fullerGeo, fullerMat);
                fuller.position.y = isAnime ? 0.82 : 0.75;
                group.add(fuller);

                // 3. Glowing Runes along the blade
                if (hasRunes) {
                    var runeMat = new THREE.MeshStandardMaterial({
                        color: runeColor,
                        emissive: runeEmissive,
                        emissiveIntensity: 1.8,
                        roughness: 0.2,
                        metalness: 0.1
                    });

                    var runeYPositions = [0.15, 0.50, 0.85, 1.20, 1.55];
                    var zSides = [0.042, -0.042];

                    for (var ri = 0; ri < runeYPositions.length; ri++) {
                        var ry = runeYPositions[ri];
                        for (var zi = 0; zi < zSides.length; zi++) {
                            var rz = zSides[zi];
                            var runeGroup = new THREE.Group();
                            runeGroup.position.set(0, ry, rz);

                            if (ri === 0) {
                                // Algiz / Divine Protection Rune (Stem + 2 angled branches)
                                var stem = new THREE.Mesh(new THREE.BoxGeometry(0.016, 0.18, 0.012), runeMat);
                                runeGroup.add(stem);
                                var br1 = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.09, 0.012), runeMat);
                                br1.position.set(-0.025, 0.04, 0);
                                br1.rotation.z = Math.PI / 4;
                                runeGroup.add(br1);
                                var br2 = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.09, 0.012), runeMat);
                                br2.position.set(0.025, 0.04, 0);
                                br2.rotation.z = -Math.PI / 4;
                                runeGroup.add(br2);
                            } else if (ri === 1) {
                                // Ingwaz / Power Diamond Rune
                                var dia = new THREE.Mesh(new THREE.OctahedronGeometry(0.055, 0), runeMat);
                                dia.scale.set(1.0, 1.2, 0.2);
                                runeGroup.add(dia);
                            } else if (ri === 2) {
                                // Sowilo / Lightning Arc Rune
                                var seg1 = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.08, 0.012), runeMat);
                                seg1.position.set(-0.02, 0.04, 0);
                                runeGroup.add(seg1);
                                var segMid = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.07, 0.012), runeMat);
                                segMid.rotation.z = Math.PI / 3;
                                runeGroup.add(segMid);
                                var seg2 = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.08, 0.012), runeMat);
                                seg2.position.set(0.02, -0.04, 0);
                                runeGroup.add(seg2);
                            } else if (ri === 3) {
                                // Tiwaz / Warrior's Arrow Rune
                                var tStem = new THREE.Mesh(new THREE.BoxGeometry(0.016, 0.18, 0.012), runeMat);
                                runeGroup.add(tStem);
                                var arrow1 = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.08, 0.012), runeMat);
                                arrow1.position.set(-0.025, 0.06, 0);
                                arrow1.rotation.z = -Math.PI / 4;
                                runeGroup.add(arrow1);
                                var arrow2 = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.08, 0.012), runeMat);
                                arrow2.position.set(0.025, 0.06, 0);
                                arrow2.rotation.z = Math.PI / 4;
                                runeGroup.add(arrow2);
                            } else {
                                // Odal / Ancient Heritage Rune
                                var oDia = new THREE.Mesh(new THREE.OctahedronGeometry(0.045, 0), runeMat);
                                oDia.scale.set(1.0, 1.0, 0.2);
                                oDia.position.y = 0.03;
                                runeGroup.add(oDia);
                                var leg1 = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.07, 0.012), runeMat);
                                leg1.position.set(-0.025, -0.05, 0);
                                leg1.rotation.z = Math.PI / 6;
                                runeGroup.add(leg1);
                                var leg2 = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.07, 0.012), runeMat);
                                leg2.position.set(0.025, -0.05, 0);
                                leg2.rotation.z = -Math.PI / 6;
                                runeGroup.add(leg2);
                            }
                            group.add(runeGroup);
                        }
                    }

                    // Mystic rune light
                    var runePointLight = new THREE.PointLight(runeEmissive, 0.9, 2.5);
                    runePointLight.position.set(0, 0.85, 0.35);
                    group.add(runePointLight);
                }

                // 4. Pointed Tip
                var tipRadius = isAnime ? 0.30 : 0.27;
                var tipLength = isAnime ? 0.68 : 0.55;
                var tipGeo = new THREE.ConeGeometry(tipRadius, tipLength, 4);
                var tip = new THREE.Mesh(tipGeo, bladeMat);
                tip.rotation.y = Math.PI / 4;
                tip.position.y = isAnime ? 2.42 : 2.22;
                group.add(tip);

                // 5. Crossguard / Guard
                if (isAnime) {
                    // Stylized Winged Anime Crossguard
                    var guardCenterGeo = new THREE.BoxGeometry(0.55, 0.22, 0.32);
                    var guardCenter = new THREE.Mesh(guardCenterGeo, guardMat);
                    guardCenter.position.y = -0.42;
                    group.add(guardCenter);

                    // Swept forward anime wings
                    var leftWing = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.16, 0.24), guardMat);
                    leftWing.position.set(-0.50, -0.36, 0);
                    leftWing.rotation.z = -0.18;
                    group.add(leftWing);

                    var rightWing = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.16, 0.24), guardMat);
                    rightWing.position.set(0.50, -0.36, 0);
                    rightWing.rotation.z = 0.18;
                    group.add(rightWing);

                    // Wingtip sharp quillon spikes
                    var tipLeft = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.26, 4), darkAccentMat);
                    tipLeft.position.set(-0.80, -0.27, 0);
                    tipLeft.rotation.z = Math.PI / 2 - 0.2;
                    group.add(tipLeft);

                    var tipRight = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.26, 4), darkAccentMat);
                    tipRight.position.set(0.80, -0.27, 0);
                    tipRight.rotation.z = -Math.PI / 2 + 0.2;
                    group.add(tipRight);

                    // Habaki / Blade Collar
                    var collar = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.14, 0.14), guardMat);
                    collar.position.y = -0.27;
                    group.add(collar);
                } else {
                    // Classic Crossguard
                    var guardCenterGeo = new THREE.BoxGeometry(1.25, 0.16, 0.28);
                    var guard = new THREE.Mesh(guardCenterGeo, guardMat);
                    guard.position.y = -0.42;
                    group.add(guard);

                    var qLeftGeo = new THREE.CylinderGeometry(0.12, 0.12, 0.24, 8);
                    var qLeft = new THREE.Mesh(qLeftGeo, guardMat);
                    qLeft.position.set(-0.62, -0.38, 0);
                    group.add(qLeft);
                    var qRight = new THREE.Mesh(qLeftGeo, guardMat);
                    qRight.position.set(0.62, -0.38, 0);
                    group.add(qRight);
                }

                // 6. Gemstone on Crossguard & Pommel (Sapphire / Ruby / Emerald)
                if (hasGem) {
                    var gemMat = new THREE.MeshStandardMaterial({
                        color: gemColor,
                        roughness: 0.08,
                        metalness: 0.15,
                        emissive: gemEmissive,
                        emissiveIntensity: 1.1,
                        transparent: true,
                        opacity: 0.94
                    });

                    // Embed gem on both front and back sides of guard center
                    var gemSides = [0.17, -0.17];
                    for (var gsi = 0; gsi < gemSides.length; gsi++) {
                        var gz = gemSides[gsi];

                        // Gold Ornate Socket Bezel
                        var bezelGeo = new THREE.CylinderGeometry(0.19, 0.21, 0.05, 8);
                        var bezel = new THREE.Mesh(bezelGeo, guardMat);
                        bezel.rotation.x = Math.PI / 2;
                        bezel.position.set(0, -0.42, gz * 0.85);
                        group.add(bezel);

                        // Faceted Brilliant Octahedron Gemstone
                        var gemGeo = new THREE.OctahedronGeometry(0.14, 0);
                        var gemMesh = new THREE.Mesh(gemGeo, gemMat);
                        gemMesh.scale.set(1.0, 1.25, 0.85);
                        gemMesh.position.set(0, -0.42, gz);
                        group.add(gemMesh);

                        // 4 Golden setting prongs
                        for (var pi = 0; pi < 4; pi++) {
                            var prongGeo = new THREE.CylinderGeometry(0.018, 0.018, 0.06, 6);
                            var prong = new THREE.Mesh(prongGeo, guardMat);
                            var pAngle = (pi * Math.PI) / 2 + Math.PI / 4;
                            prong.position.set(Math.cos(pAngle) * 0.12, -0.42 + Math.sin(pAngle) * 0.12, gz * 0.95);
                            prong.rotation.x = Math.PI / 2;
                            group.add(prong);
                        }
                    }

                    // Gem Point Light casting soft colored illumination
                    var gemLight = new THREE.PointLight(gemEmissive, 1.2, 2.2);
                    gemLight.position.set(0, -0.42, 0.45);
                    group.add(gemLight);

                    // Matching Secondary Gemstone on Pommel
                    var pommelGemGeo = new THREE.OctahedronGeometry(0.08, 0);
                    var pommelGemFront = new THREE.Mesh(pommelGemGeo, gemMat);
                    pommelGemFront.position.set(0, -1.45, 0.17);
                    group.add(pommelGemFront);
                    var pommelGemBack = new THREE.Mesh(pommelGemGeo, gemMat);
                    pommelGemBack.position.set(0, -1.45, -0.17);
                    group.add(pommelGemBack);
                }

                // 7. Grip / Hilt
                var gripGeo = new THREE.CylinderGeometry(0.09, 0.1, 0.88, 12);
                var gripMat = new THREE.MeshStandardMaterial({ color: gripColor, roughness: 0.85, metalness: 0.1 });
                var grip = new THREE.Mesh(gripGeo, gripMat);
                grip.position.y = -0.92;
                group.add(grip);

                // 8. Grip Wrap / Rings
                var ringMat = new THREE.MeshStandardMaterial({
                    color: isAnime ? 0xD4AF37 : 0x3E2214,
                    roughness: 0.4,
                    metalness: isAnime ? 0.8 : 0.0
                });
                for (var rIdx = 0; rIdx < 4; rIdx++) {
                    var ringGeo = new THREE.TorusGeometry(0.105, 0.02, 8, 16);
                    var rMesh = new THREE.Mesh(ringGeo, ringMat);
                    rMesh.rotation.x = Math.PI / 2;
                    rMesh.position.y = -0.65 - (rIdx * 0.18);
                    group.add(rMesh);
                }

                // 9. Pommel
                var pommelGeo = isAnime ? new THREE.DodecahedronGeometry(0.20, 0) : new THREE.SphereGeometry(0.2, 16, 16);
                var pommel = new THREE.Mesh(pommelGeo, guardMat);
                pommel.position.y = -1.45;
                group.add(pommel);

                // Hero angle presentation
                group.scale.setScalar(0.85);
                group.rotation.z = -0.32;
                group.rotation.x = 0.08;
                group.position.set(0, -0.05, 0);

            } else if (isAxe) {
                var shaftGeo = new THREE.CylinderGeometry(0.08, 0.1, 2.8, 12);
                var shaftMat = new THREE.MeshStandardMaterial({ color: 0x8B5A2B, roughness: 0.7, metalness: 0.1 });
                var shaft = new THREE.Mesh(shaftGeo, shaftMat);
                group.add(shaft);

                var axeHeadGeo = new THREE.BoxGeometry(0.8, 0.9, 0.15);
                var headMat = new THREE.MeshStandardMaterial({ color: 0xCBD5E1, roughness: 0.3, metalness: 0.7 });
                var axeHead = new THREE.Mesh(axeHeadGeo, headMat);
                axeHead.position.set(0.42, 0.9, 0);
                group.add(axeHead);

            } else if (isShield) {
                var shieldGeo = new THREE.BoxGeometry(1.6, 2.2, 0.15);
                var shieldMat = new THREE.MeshStandardMaterial({ color: 0x2563EB, roughness: 0.5, metalness: 0.2 });
                var shield = new THREE.Mesh(shieldGeo, shieldMat);
                group.add(shield);

                var bossGeo = new THREE.SphereGeometry(0.35, 16, 16);
                var bossMat = new THREE.MeshStandardMaterial({ color: 0xD4AF37, roughness: 0.3, metalness: 0.8 });
                var boss = new THREE.Mesh(bossGeo, bossMat);
                boss.position.z = 0.12;
                group.add(boss);

            } else if (isChest) {
                var baseGeo = new THREE.BoxGeometry(2.0, 1.2, 1.4);
                var baseMat = new THREE.MeshStandardMaterial({ color: 0x8B4513, roughness: 0.6, metalness: 0.1 });
                var baseMesh = new THREE.Mesh(baseGeo, baseMat);
                baseMesh.position.y = -0.4;
                group.add(baseMesh);

                var lidGeo = new THREE.CylinderGeometry(0.7, 0.7, 2.0, 16, 1, false, 0, Math.PI);
                var lidMat = new THREE.MeshStandardMaterial({ color: 0xA0522D, roughness: 0.5 });
                var lidMesh = new THREE.Mesh(lidGeo, lidMat);
                lidMesh.rotation.z = Math.PI / 2;
                lidMesh.position.y = 0.2;
                group.add(lidMesh);

                var bandGeo = new THREE.BoxGeometry(0.15, 1.25, 1.45);
                var bandMat = new THREE.MeshStandardMaterial({ color: 0xD4AF37, metalness: 0.8, roughness: 0.3 });
                var band1 = new THREE.Mesh(bandGeo, bandMat);
                band1.position.set(-0.6, -0.4, 0);
                group.add(band1);
                var band2 = new THREE.Mesh(bandGeo, bandMat);
                band2.position.set(0.6, -0.4, 0);
                group.add(band2);

            } else if (isHouse) {
                var houseBaseGeo = new THREE.BoxGeometry(2.4, 1.8, 2.0);
                var houseBaseMat = new THREE.MeshStandardMaterial({ color: 0xFBBF24, roughness: 0.7, metalness: 0.05 });
                var houseBase = new THREE.Mesh(houseBaseGeo, houseBaseMat);
                group.add(houseBase);

                var roofGeo = new THREE.ConeGeometry(2.1, 1.3, 4);
                var roofMat = new THREE.MeshStandardMaterial({ color: 0xDC2626, roughness: 0.5, metalness: 0.1 });
                var roof = new THREE.Mesh(roofGeo, roofMat);
                roof.rotation.y = Math.PI / 4;
                roof.position.y = 1.5;
                group.add(roof);

                var doorGeo = new THREE.BoxGeometry(0.6, 1.0, 0.1);
                var doorMat = new THREE.MeshStandardMaterial({ color: 0x78350F, roughness: 0.8 });
                var door = new THREE.Mesh(doorGeo, doorMat);
                door.position.set(0, -0.4, 1.02);
                group.add(door);

            } else if (isTree) {
                var trunkGeo = new THREE.CylinderGeometry(0.25, 0.35, 1.6, 12);
                var trunkMat = new THREE.MeshStandardMaterial({ color: 0x78350F, roughness: 0.9 });
                var trunk = new THREE.Mesh(trunkGeo, trunkMat);
                trunk.position.y = -0.5;
                group.add(trunk);

                var folMat = new THREE.MeshStandardMaterial({ color: 0x16A34A, roughness: 0.6 });
                var fol1 = new THREE.Mesh(new THREE.ConeGeometry(1.6, 1.4, 8), folMat);
                fol1.position.y = 0.4;
                group.add(fol1);
                var fol2 = new THREE.Mesh(new THREE.ConeGeometry(1.2, 1.2, 8), folMat);
                fol2.position.y = 1.2;
                group.add(fol2);
                var fol3 = new THREE.Mesh(new THREE.ConeGeometry(0.8, 1.0, 8), folMat);
                fol3.position.y = 1.9;
                group.add(fol3);

            } else if (isCar) {
                var carBodyGeo = new THREE.BoxGeometry(3.0, 0.6, 1.6);
                var carBodyMat = new THREE.MeshStandardMaterial({ color: 0xEF4444, roughness: 0.3, metalness: 0.4 });
                var carBody = new THREE.Mesh(carBodyGeo, carBodyMat);
                group.add(carBody);

                var cabinGeo = new THREE.BoxGeometry(1.6, 0.7, 1.4);
                var cabinMat = new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.2, metalness: 0.8 });
                var cabin = new THREE.Mesh(cabinGeo, cabinMat);
                cabin.position.set(-0.2, 0.6, 0);
                group.add(cabin);

                var wheelGeo = new THREE.CylinderGeometry(0.35, 0.35, 0.25, 16);
                var wheelMat = new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.9 });
                var wPositions = [
                    [-0.9, -0.3, 0.85], [0.9, -0.3, 0.85],
                    [-0.9, -0.3, -0.85], [0.9, -0.3, -0.85]
                ];
                wPositions.forEach(function(pos) {
                    var w = new THREE.Mesh(wheelGeo, wheelMat);
                    w.rotation.x = Math.PI / 2;
                    w.position.set(pos[0], pos[1], pos[2]);
                    group.add(w);
                });

            } else if (isGun) {
                var gunBodyGeo = new THREE.BoxGeometry(1.8, 0.6, 0.4);
                var gunBodyMat = new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.4, metalness: 0.6 });
                var gunBody = new THREE.Mesh(gunBodyGeo, gunBodyMat);
                group.add(gunBody);

                var barrelGeo = new THREE.CylinderGeometry(0.12, 0.12, 1.2, 16);
                var barrelMat = new THREE.MeshStandardMaterial({ color: 0x06B6D4, roughness: 0.2, metalness: 0.9 });
                var barrel = new THREE.Mesh(barrelGeo, barrelMat);
                barrel.rotation.z = Math.PI / 2;
                barrel.position.set(1.2, 0.1, 0);
                group.add(barrel);

                var gunGripGeo = new THREE.BoxGeometry(0.4, 0.9, 0.35);
                var gunGrip = new THREE.Mesh(gunGripGeo, gunBodyMat);
                gunGrip.rotation.z = 0.2;
                gunGrip.position.set(-0.5, -0.6, 0);
                group.add(gunGrip);

            } else {
                var crystalGeo = new THREE.IcosahedronGeometry(1.3, 0);
                var crystalMat = new THREE.MeshStandardMaterial({
                    color: 0x9333EA,
                    roughness: 0.2,
                    metalness: 0.6,
                    flatShading: true
                });
                var crystal = new THREE.Mesh(crystalGeo, crystalMat);
                group.add(crystal);

                var ringGeo = new THREE.TorusGeometry(1.8, 0.06, 16, 64);
                var ringMat = new THREE.MeshStandardMaterial({ color: 0x06B6D4, roughness: 0.3, metalness: 0.9 });
                var ring = new THREE.Mesh(ringGeo, ringMat);
                ring.rotation.x = Math.PI / 3;
                group.add(ring);
            }

            return group;
        })(prompt);
    """)
}


/** Height of the manikin's root joint in the scene; its soles stand at RIG_ROOT_Y + R15Skeleton.SOLE_Y. */
const val RIG_ROOT_Y = -0.3

/** The palm's grip point in the hand joint's frame; the server's two-handed hold uses the same point. */
private object AnimationWeaponsGrip { const val x = 0.0; const val y = -0.25; const val z = 0.0 }

/**
 * Built-in weapons, blocky like the manikin, in the hand's frame: the grip at the origin, pointing along -Z
 * (forward when the arm hangs, like a Roblox Tool) and up along +Y.
 */
private val buildWeaponFunction: dynamic = js("""
    (function(kind) {
        var T = window.THREE;
        var metal = new T.MeshStandardMaterial({ color: 0xcbd5e1, roughness: 0.3, metalness: 0.7 });
        var wood = new T.MeshStandardMaterial({ color: 0x92400e, roughness: 0.7, metalness: 0.05 });
        var dark = new T.MeshStandardMaterial({ color: 0x1f2937, roughness: 0.5, metalness: 0.4 });
        var gold = new T.MeshStandardMaterial({ color: 0xd4a017, roughness: 0.35, metalness: 0.8 });
        var g = new T.Group();
        function box(w, h, d, mat, x, y, z) { var m = new T.Mesh(new T.BoxGeometry(w, h, d), mat); m.position.set(x, y, z); g.add(m); return m; }
        if (kind === 'AXE') {
            box(0.16, 0.16, 3.4, wood, 0, 0, -1.2);
            box(0.1, 1.3, 0.9, metal, 0, 0.35, -2.55);
        } else if (kind === 'STAFF') {
            box(0.16, 0.16, 5.0, wood, 0, 0, -0.9);
            box(0.45, 0.45, 0.45, gold, 0, 0, -3.5);
        } else if (kind === 'GUN') {
            box(0.25, 0.55, 0.3, dark, 0, 0.05, 0.05);
            box(0.35, 0.4, 1.5, dark, 0, 0.45, -0.45);
            box(0.18, 0.18, 0.8, metal, 0, 0.5, -1.5);
        } else {
            box(0.14, 0.14, 0.9, wood, 0, 0, 0.1);
            box(0.9, 0.16, 0.18, gold, 0, 0, -0.4);
            box(0.08, 0.38, 3.0, metal, 0, 0, -2.0);
        }
        return g;
    })
""")

/** Typical length of each weapon kind in studs: a held model of the user's is scaled to it. */
private fun weaponLength(kind: WeaponKind): Double = when (kind) {
    WeaponKind.SWORD -> 3.6
    WeaponKind.AXE -> 3.4
    WeaponKind.STAFF -> 5.0
    WeaponKind.GUN -> 2.2
}

/**
 * A loaded model of the user's as a held weapon: its longest side turned to -Z (generated weapons stand upright),
 * scaled to [length], the grip a little in from the end that was at the bottom.
 */
private val holdModelFunction: dynamic = js("""
    (function(scene, length) {
        var T = window.THREE;
        // One "new" per statement: the Kotlin/JS printer drops it in "new A().method()".
        function boxOf(object) { var b = new T.Box3(); b.setFromObject(object); return b; }
        function sizeOf(box) { var v = new T.Vector3(); box.getSize(v); return v; }
        var inner = new T.Group();
        inner.add(scene);
        var size = sizeOf(boxOf(inner));
        // The longest side becomes the weapon's length (along Z).
        if (size.y >= size.x && size.y >= size.z) inner.rotation.x = -Math.PI / 2;
        else if (size.x >= size.z) inner.rotation.y = Math.PI / 2;
        inner.updateMatrixWorld(true);
        size = sizeOf(boxOf(inner));
        inner.scale.setScalar(length / Math.max(size.z, 0.01));
        var oriented = new T.Group();
        oriented.add(inner);
        oriented.updateMatrixWorld(true);
        // The handle is the thinner end (a bat, an axe, a hammer): the widest point near each end decides.
        var box = boxOf(oriented);
        var cx = (box.min.x + box.max.x) / 2, cy = (box.min.y + box.max.y) / 2, span = box.max.z - box.min.z;
        var nearMax = 0, nearMin = 0, point = new T.Vector3();
        oriented.traverse(function(child) {
            if (!child.isMesh || !child.geometry || !child.geometry.attributes.position) return;
            var positions = child.geometry.attributes.position;
            var step = Math.max(1, Math.floor(positions.count / 4000));
            for (var i = 0; i < positions.count; i += step) {
                point.fromBufferAttribute(positions, i).applyMatrix4(child.matrixWorld);
                var r = Math.max(Math.abs(point.x - cx), Math.abs(point.y - cy));
                if (point.z > box.max.z - span * 0.15) nearMax = Math.max(nearMax, r);
                if (point.z < box.min.z + span * 0.15) nearMin = Math.max(nearMin, r);
            }
        });
        if (nearMin < nearMax * 0.8) oriented.rotation.y = Math.PI;
        var holder = new T.Group();
        holder.add(oriented);
        holder.updateMatrixWorld(true);
        box = boxOf(oriented);
        var c = new T.Vector3();
        box.getCenter(c);
        // The grip a little in from the handle's end; the weapon points forward (-Z) from the hand.
        var grip = box.max.z - (box.max.z - box.min.z) * 0.12;
        oriented.position.set(-c.x, -c.y, -grip);
        return holder;
    })
""")

// A self-contained function called with arguments: a js("…") string must not name Kotlin parameters or locals,
// the compiler renames them.
private val buildRigFunction: dynamic = js("""
    (function(sc, segs, rootY, modelUrl) {
        var T = window.THREE;
        function mat(color, rough) { return new T.MeshStandardMaterial({ color: color, roughness: rough, metalness: 0.02 }); }
        var mats = { shirt: mat(0x6366f1, 0.7), skin: mat(0xf6c89f, 0.6), pants: mat(0x334155, 0.75), shoe: mat(0x1e293b, 0.5) };

        // Face picture: skin all around, a neutral face - two dark eyes and a short straight mouth.
        function faceMaterial() {
            var c = document.createElement('canvas'); c.width = 256; c.height = 256;
            var x = c.getContext('2d');
            x.fillStyle = '#f6c89f'; x.fillRect(0, 0, 256, 256);
            x.fillStyle = '#1f1a17';
            [-1, 1].forEach(function(side) { x.beginPath(); x.ellipse(128 + side * 34, 118, 9, 15, 0, 0, Math.PI * 2); x.fill(); });
            x.strokeStyle = '#3a2a22'; x.lineWidth = 6; x.lineCap = 'round';
            x.beginPath(); x.moveTo(110, 160); x.lineTo(146, 160); x.stroke();
            var tex = new T.CanvasTexture(c);
            if (T.sRGBEncoding) tex.encoding = T.sRGBEncoding;
            tex.anisotropy = 4;
            return new T.MeshStandardMaterial({ map: tex, roughness: 0.6, metalness: 0.02 });
        }

        // The face goes on the front (-Z) of the head, projected straight on; the rest of the head samples skin.
        function faceUv(geo) {
            geo.computeBoundingBox();
            var b = geo.boundingBox, w = b.max.x - b.min.x, h = b.max.y - b.min.y, cy = (b.max.y + b.min.y) / 2;
            var pos = geo.attributes.position, uv = new Float32Array(pos.count * 2);
            for (var i = 0; i < pos.count; i++) {
                // Seen from the front the character's right (+X) is on the picture's left.
                if (pos.getZ(i) < 0) { uv[i * 2] = 0.5 - pos.getX(i) / w; uv[i * 2 + 1] = 0.5 + (pos.getY(i) - cy) / h; }
                else { uv[i * 2] = 0.02; uv[i * 2 + 1] = 0.02; }
            }
            geo.setAttribute('uv', new T.BufferAttribute(uv, 2));
        }

        function materialFor(joint) {
            if (joint === 'UpperTorso' || /UpperArm$/.test(joint)) return mats.shirt;
            if (/LowerArm$/.test(joint) || /Hand$/.test(joint)) return mats.skin;
            if (/Foot$/.test(joint)) return mats.shoe;
            return mats.pants;
        }

        var root = new T.Group();
        root.position.set(0, rootY, 0);
        sc.add(root);
        var pivots = {};
        segs.forEach(function(s) {
            var g = new T.Group();
            g.position.set(s[2], s[3], s[4]);
            (s[1] ? pivots[s[1]] : root).add(g);
            pivots[s[0]] = g;
        });

        // Roblox's R15 parts, each already in its joint's frame: hung on the joint as they are (a rigid binding,
        // like a Motor6D), so a part can never drift away from its joint. Loaded once per page.
        if (!window.__r15AvatarPromise) {
            window.__r15AvatarPromise = new Promise(function(resolve, reject) {
                var tries = 0;
                // The GLTF loader script may still be on its way.
                (function load() {
                    if (!T.GLTFLoader) { if (++tries < 100) setTimeout(load, 100); else reject('no GLTFLoader'); return; }
                    // Created on its own line: the Kotlin/JS printer drops the "new" in "new A().load(...)".
                    var loader = new T.GLTFLoader();
                    loader.load(modelUrl, function(gltf) {
                        var parts = {};
                        gltf.scene.traverse(function(o) { if (o.isMesh) parts[o.name] = o.geometry; });
                        if (parts.Head) faceUv(parts.Head);
                        resolve(parts);
                    }, undefined, reject);
                })();
            });
        }
        window.__r15AvatarPromise.then(function(parts) {
            var face = faceMaterial();
            Object.keys(pivots).forEach(function(joint) {
                var geo = parts[joint];
                if (!geo) return;
                var mesh = new T.Mesh(geo, joint === 'Head' ? face : materialFor(joint));
                mesh.name = 'r15:' + joint;
                pivots[joint].add(mesh);
            });
        }, function(error) { window.__r15AvatarPromise = null; console.warn('R15 avatar', error); });
        return pivots;
    })
""")

/** The R15 avatar's parts (scripts/build_r15_avatar.py), served with the site. */
private const val R15_AVATAR_URL = "/models/r15-avatar.glb"

private fun buildRigJs(scene: dynamic, segments: Array<Array<Any?>>, rootY: Double): dynamic =
    buildRigFunction(scene, segments, rootY, R15_AVATAR_URL)

data class RigBone(
    val mesh: dynamic,
    val defaultPosition: dynamic,
    val defaultQuaternion: dynamic,
    val currentPosition: dynamic,
    val currentQuaternion: dynamic
)

/**
 * A bone of an own character driven by R15 poses. [restWorld] is its rest turn in the world, [toR15] turns its own
 * limb direction back to the R15 one, [restPosition] is where it rests in the world (the root moves from there).
 */
class CharacterBone(val bone: dynamic, val restWorld: dynamic, val toR15: dynamic, val restPosition: dynamic)

fun applyPoseToRig(
    timeSeconds: Double,
    rig: Map<String, RigBone>,
    keyframes: List<Keyframe>
) {
    if (keyframes.isEmpty()) return

    for ((boneName, bone) in rig) {
        val surrounding = findSurroundingKeyframes(boneName, timeSeconds, keyframes)
        val p0Keyframe = surrounding.prev ?: surrounding.next
        val p1Keyframe = surrounding.next ?: surrounding.prev

        if (p0Keyframe == null || p1Keyframe == null) {
            bone.mesh.position.copy(bone.defaultPosition)
            bone.mesh.quaternion.copy(bone.defaultQuaternion)
            continue
        }

        val t0 = p0Keyframe.time.toDouble()
        val t1 = p1Keyframe.time.toDouble()
        val rawT = if (t1 > t0) ((timeSeconds - t0) / (t1 - t0)).coerceIn(0.0, 1.0) else 0.0
        val t = applyEasing(rawT, p0Keyframe.easingStyle, p0Keyframe.easingDirection)

        val p0 = p0Keyframe.poses[boneName]
        val p1 = p1Keyframe.poses[boneName]

        val basePose = PoseDto(
            position = Triple(0.0, 0.0, 0.0),
            rotation = Triple(0.0, 0.0, 0.0)
        )
        val posA = p0 ?: basePose
        val posB = p1 ?: p0 ?: basePose

        val px = posA.position.first + (posB.position.first - posA.position.first) * t
        val py = posA.position.second + (posB.position.second - posA.position.second) * t
        val pz = posA.position.third + (posB.position.third - posA.position.third) * t

        val rxA = posA.rotation.first
        val ryA = posA.rotation.second
        val rzA = posA.rotation.third
        val rxB = posB.rotation.first
        val ryB = posB.rotation.second
        val rzB = posB.rotation.third

        // Plain dynamic calls: a js("…") string must not name Kotlin locals, the compiler renames them.
        val quatA = js("new window.THREE.Quaternion()")
        quatA.setFromEuler(js("new window.THREE.Euler()").set(rxA, ryA, rzA, "XYZ"))
        val quatB = js("new window.THREE.Quaternion()")
        quatB.setFromEuler(js("new window.THREE.Euler()").set(rxB, ryB, rzB, "XYZ"))
        quatA.slerp(quatB, t)
        bone.currentPosition.copy(bone.defaultPosition).add(js("new window.THREE.Vector3()").set(px, py, pz))
        bone.currentQuaternion.copy(bone.defaultQuaternion).multiply(quatA)
        bone.mesh.position.copy(bone.currentPosition)
        bone.mesh.quaternion.copy(bone.currentQuaternion)
    }
}

/** A joint's turn (quaternion) and root offset (studs) at [timeSeconds], or null when the clip never keys it. */
private fun poseAt(joint: String, timeSeconds: Double, keyframes: List<Keyframe>): Pair<dynamic, dynamic>? {
    val surrounding = findSurroundingKeyframes(joint, timeSeconds, keyframes)
    val a = surrounding.prev ?: surrounding.next ?: return null
    val b = surrounding.next ?: surrounding.prev ?: return null
    val t0 = a.time.toDouble()
    val t1 = b.time.toDouble()
    val k = applyEasing(if (t1 > t0) ((timeSeconds - t0) / (t1 - t0)).coerceIn(0.0, 1.0) else 0.0, a.easingStyle, a.easingDirection)
    val rest = PoseDto(Triple(0.0, 0.0, 0.0), Triple(0.0, 0.0, 0.0))
    val pa = a.poses[joint] ?: rest
    val pb = b.poses[joint] ?: pa
    val qa = js("new window.THREE.Quaternion()")
    qa.setFromEuler(js("new window.THREE.Euler()").set(pa.rotation.first, pa.rotation.second, pa.rotation.third, "XYZ"))
    val qb = js("new window.THREE.Quaternion()")
    qb.setFromEuler(js("new window.THREE.Euler()").set(pb.rotation.first, pb.rotation.second, pb.rotation.third, "XYZ"))
    qa.slerp(qb, k)
    val offset = js("new window.THREE.Vector3()").set(
        pa.position.first + (pb.position.first - pa.position.first) * k,
        pa.position.second + (pb.position.second - pa.position.second) * k,
        pa.position.third + (pb.position.third - pa.position.third) * k
    )
    // Pair(...), not "qa to offset": with a dynamic receiver Kotlin/JS would call a JS method named "to".
    return Pair(qa, offset)
}

/**
 * Plays R15 poses on an own character. G is the turn the R15 figure would have in the world; the bone gets
 * G · toR15 · restWorld, so its limb points where the R15 limb points whatever its own rest turn (T-pose arms,
 * Meshy's rotated bones); its local turn follows from its parent's current world turn (in-between bones like
 * Meshy's spine just follow). The root's offset is applied in the world, so a scaled armature (cm) does not matter.
 */
fun applyPoseToCharacter(timeSeconds: Double, bones: Map<String, CharacterBone>, keyframes: List<Keyframe>) {
    if (keyframes.isEmpty()) return
    val world = mutableMapOf<String, dynamic>()
    fun drive(joint: String, parentJoint: String?) {
        val target = bones[joint] ?: return
        val pose = poseAt(joint, timeSeconds, keyframes)
        val turn: dynamic = pose?.first ?: js("new window.THREE.Quaternion()")
        val parentTurn: dynamic = parentJoint?.let { world[it] }
        val g: dynamic = if (parentTurn != null) parentTurn.clone().multiply(turn) else turn
        world[joint] = g
        val wanted: dynamic = g.clone().multiply(target.toR15).multiply(target.restWorld)
        val parent = target.bone.parent
        parent.updateWorldMatrix(true, false)
        val parentWorld: dynamic = parent.getWorldQuaternion(js("new window.THREE.Quaternion()"))
        target.bone.quaternion.copy(parentWorld.invert().multiply(wanted))
        if (parentJoint == null) {
            val place: dynamic = target.restPosition.clone()
            if (pose != null) place.add(pose.second)
            target.bone.position.copy(parent.worldToLocal(place))
        }
        target.bone.updateMatrixWorld(true)
    }
    for (segment in R15Skeleton.SEGMENTS) drive(segment.joint, segment.parent)
    // Finger bones, when the character has them, after their hands.
    for (finger in R15Fingers.BONES) drive(finger.name, finger.parent)
}

/** Scene helpers must never take the page down: a failing one is logged and skipped. */
private inline fun sceneCall(what: String, block: () -> Unit) {
    try {
        block()
    } catch (e: Throwable) {
        console.error("[MeshViewer] $what failed", e)
    }
}

private fun slugify(input: String): String =
    input.lowercase()
        .replace(Regex("[^a-z0-9_-]+"), "_")
        .trim('_')
        .ifEmpty { "asset" }

private fun downloadBlob(filename: String, content: String, mime: String = "text/plain") {
    try {
        val blob = Blob(arrayOf(content), BlobPropertyBag(type = mime))
        val url = URL.createObjectURL(blob)
        val a = document.createElement("a")
        a.setAttribute("href", url)
        a.setAttribute("download", filename)
        document.body?.appendChild(a)
        a.asDynamic().click()
        document.body?.removeChild(a)
        URL.revokeObjectURL(url)
    } catch (e: Throwable) {
        js("console.error('[MeshViewer.export] downloadBlob error', e)")
    }
}

@Composable
fun MeshViewer(
    modelUrl: String? = null,
    prompt: String = "",
    modifier: Modifier = Modifier,
    assetType: AssetType = AssetType.MESH_3D,
    animationKeyframesJson: String? = null,
    /** The animation's settings (AnimationSetup metadata): weapons shown in the manikin's hands. */
    animationMetadata: Map<String, String> = emptyMap(),
    showTimeline: Boolean = false,
    initialIsPlaying: Boolean = false,
    initialSpeed: Double = 1.0,
    initialLoop: Boolean = true,
    /** Shared with the panel under the viewer; a private one when not given. */
    viewState: MeshViewState? = null,
    /** Shared by viewers that should move together (version comparison). */
    cameraState: MeshCameraState? = null,
    /** Hides the top and bottom overlays (small comparison viewers). */
    compact: Boolean = false,
    initialHeightPx: Int = 440
) {
    val theme = LocalAppTheme.current
    val s = strings()
    val palette = theme.colors

    val isAnimationMode = assetType == AssetType.ANIMATION

    var isFullscreen by remember { mutableStateOf(false) }
    var viewerHeightPx by remember { mutableStateOf(initialHeightPx) }
    var isResizingHeight by remember { mutableStateOf(false) }
    var startDragY by remember { mutableStateOf(0.0) }
    var startHeight by remember { mutableStateOf(440) }

    val view = viewState ?: remember { MeshViewState() }
    var shadingMode by view::shading
    var lightingPreset by view::lighting
    var showGrid by view::showGrid
    var showAxes by remember { mutableStateOf(true) }
    var autoRotate by view::autoRotate

    var modelStats by view::stats

    // The R15 manikin is taller than a normalized model: start further away so it fits.
    val defaultZoom = if (isAnimationMode) ANIMATION_ZOOM else DEFAULT_ZOOM
    val camera = cameraState ?: remember { MeshCameraState(DEFAULT_ROT_X, DEFAULT_ROT_Y, defaultZoom) }
    var rotX by camera.rotX
    var rotY by camera.rotY
    var zoomDist by camera.zoom
    var targetX by camera.targetX
    var targetY by camera.targetY
    var targetZ by camera.targetZ

    // Where the loaded model sits in the scene: it is scaled to a fixed size, real studs are kept for the helpers.
    var modelScale by remember { mutableStateOf(1.0) }
    // The manikin of an animation stands on this floor; models move it to their own bottom when loaded.
    var groundY by remember { mutableStateOf(if (assetType == AssetType.ANIMATION) RIG_ROOT_Y + R15Skeleton.SOLE_Y else -1.6) }
    var modelRightX by remember { mutableStateOf(1.6) }
    var videoSpin by remember { mutableStateOf(false) }

    var isDraggingOrbit by remember { mutableStateOf(false) }
    var isDraggingPan by remember { mutableStateOf(false) }
    var lastMouseX by remember { mutableStateOf(0.0) }
    var lastMouseY by remember { mutableStateOf(0.0) }

    var isPlaying by remember { mutableStateOf(initialIsPlaying) }
    var currentTime by remember { mutableStateOf(0.0) }
    var speedFactor by remember { mutableStateOf(initialSpeed) }
    var loopEnabled by remember { mutableStateOf(initialLoop) }

    var animSequence by remember { mutableStateOf<KeyframeSequence?>(null) }
    var animationParseError by remember { mutableStateOf<String?>(null) }

    val animKeyframes: List<Keyframe> = remember(animSequence) {
        animSequence?.keyframes ?: emptyList()
    }
    val duration: Double = remember(animSequence) {
        animSequence?.duration?.toDouble() ?: 1.0
    }

    // Workspace keys: Space plays/pauses, arrows step between keys, Escape leaves the workspace.
    DisposableEffect(isFullscreen, isAnimationMode, animKeyframes) {
        if (!isFullscreen) return@DisposableEffect onDispose { }
        val keyTimes = animKeyframes.map { it.time.toDouble() }.distinct().sorted()
        val listener: (org.w3c.dom.events.Event) -> Unit = { event ->
            val key = event.asDynamic().key as? String
            val typing = (event.target as? org.w3c.dom.Element)?.tagName?.lowercase() in setOf("input", "textarea", "select")
            if (!typing) when (key) {
                // Only the workspace closes: the panel around it has its own Escape. The scene is rebuilt by the
                // effect that watches isFullscreen.
                "Escape" -> { event.stopPropagation(); isFullscreen = false }
                " " -> if (isAnimationMode) { event.preventDefault(); isPlaying = !isPlaying }
                "ArrowLeft" -> if (isAnimationMode) { isPlaying = false; currentTime = keyTimes.lastOrNull { it < currentTime - 0.001 } ?: 0.0 }
                "ArrowRight" -> if (isAnimationMode) { isPlaying = false; currentTime = keyTimes.firstOrNull { it > currentTime + 0.001 } ?: currentTime }
            }
        }
        // Capture phase: runs before the panel's own Escape handler.
        window.addEventListener("keydown", listener, true)
        onDispose { window.removeEventListener("keydown", listener, true) }
    }

    var canvasRef by remember { mutableStateOf<HTMLCanvasElement?>(null) }
    var isInitialized by remember { mutableStateOf(false) }
    var modelLoaded by remember { mutableStateOf(false) }

    var threeRenderer by remember { mutableStateOf<dynamic>(null) }
    var threeScene by remember { mutableStateOf<dynamic>(null) }
    var threeCamera by remember { mutableStateOf<dynamic>(null) }
    var threeMesh by remember { mutableStateOf<dynamic>(null) }
    var threeGrid by remember { mutableStateOf<dynamic>(null) }
    var threeAxes by remember { mutableStateOf<dynamic>(null) }
    var rigMap by remember { mutableStateOf<Map<String, RigBone>?>(null) }
    /** An own character's bones, driven instead of the manikin once loaded. */
    var characterRig by remember { mutableStateOf<Map<String, CharacterBone>?>(null) }

    var animFrameId by remember { mutableStateOf(0) }
    var lastFrameNs by remember { mutableStateOf(0.0) }

    LaunchedEffect(animationKeyframesJson, isAnimationMode) {
        if (!isAnimationMode) return@LaunchedEffect
        // No canned motion when the work has no keyframes (old demo works): the rig stays in its rest pose.
        val parsed = animationKeyframesJson?.takeIf { it.isNotBlank() }?.let { parseRobloxKeyframeSequenceJson(it) }
        if (parsed == null || parsed.keyframes.isEmpty()) {
            animSequence = null
            animationParseError = if (!animationKeyframesJson.isNullOrBlank()) {
                s.meshViewer.animationParseError
            } else null
            try {
                ToastManager.warning(
                    message = animationParseError ?: s.meshViewer.animationMissing,
                    title = s.meshViewer.animationPreviewTitle
                )
            } catch (_: Throwable) {}
        } else {
            animSequence = parsed
            loopEnabled = parsed.looped
        }
    }

    fun applyCameraPreset(preset: CameraViewPreset) {
        rotX = preset.rotX
        rotY = preset.rotY
        if (preset == CameraViewPreset.RESET) {
            targetX = 0.0
            targetY = 0.0
            targetZ = 0.0
        }
    }

    fun updateLighting(scene: dynamic, preset: MeshLightingPreset) {
        if (scene == null) return
        js("""
            (function(sc, pName) {
                var THREE = window.THREE;
                if (!THREE || !sc) return;
                if (sc.__lightsGroup) {
                    sc.remove(sc.__lightsGroup);
                }
                var group = new THREE.Group();
                sc.__lightsGroup = group;

                var amb = new THREE.AmbientLight(0xffffff, 0.65);
                group.add(amb);
                var hemi = new THREE.HemisphereLight(0xffffff, 0x333344, 0.55);
                group.add(hemi);

                if (pName === 'STUDIO') {
                    var key = new THREE.DirectionalLight(0xffffff, 1.2);
                    key.position.set(5, 12, 8);
                    group.add(key);
                    var fill = new THREE.DirectionalLight(0x93c5fd, 0.5);
                    fill.position.set(-6, 3, -4);
                    group.add(fill);
                    var rim = new THREE.DirectionalLight(0xd8b4fe, 0.6);
                    rim.position.set(0, -4, -6);
                    group.add(rim);
                } else if (pName === 'ROBLOX') {
                    // Close to a new Roblox place: blue sky light from above, warm sun from the upper right.
                    group.remove(hemi);
                    amb.intensity = 0.3;
                    group.add(new THREE.HemisphereLight(0xbfdcff, 0x6b6e73, 0.55));
                    var rsun = new THREE.DirectionalLight(0xfff4e0, 0.95);
                    rsun.position.set(6, 14, -4);
                    group.add(rsun);
                } else if (pName === 'NIGHT') {
                    amb.intensity = 0.06;
                    hemi.intensity = 0.08;
                    var moon = new THREE.DirectionalLight(0x9db4ff, 0.6);
                    moon.position.set(-6, 10, 6);
                    group.add(moon);
                    var lamp = new THREE.PointLight(0xffb347, 1.1, 30);
                    lamp.position.set(3, 3, -4);
                    group.add(lamp);
                } else if (pName === 'SUNLIGHT') {
                    var sun = new THREE.DirectionalLight(0xfffbeb, 1.4);
                    sun.position.set(8, 16, 8);
                    group.add(sun);
                    var bounce = new THREE.DirectionalLight(0xfde68a, 0.4);
                    bounce.position.set(-5, -2, -3);
                    group.add(bounce);
                } else if (pName === 'CYBERPUNK') {
                    var purple = new THREE.DirectionalLight(0xd946ef, 1.3);
                    purple.position.set(6, 8, 5);
                    group.add(purple);
                    var cyan = new THREE.DirectionalLight(0x06b6d4, 1.2);
                    cyan.position.set(-6, 4, -5);
                    group.add(cyan);
                    var bottom = new THREE.DirectionalLight(0x8b5cf6, 0.5);
                    bottom.position.set(0, -6, 0);
                    group.add(bottom);
                } else if (pName === 'FLAT') {
                    var flatAmb = new THREE.AmbientLight(0xffffff, 1.2);
                    group.add(flatAmb);
                }
                sc.add(group);
            })(scene, preset.name);
        """)
    }

    /**
     * R15 avatar built like the Roblox rig: every joint is a pivot group at the joint point, children hang from their
     * parent, so turning a shoulder carries the forearm and hand. Rotations follow CFrame.Angles; the character faces
     * -Z (the drawn face marks the front). The parts are Roblox's own R15 meshes, each fixed to its joint.
     */
    fun generateR15Rig(scene: dynamic): Map<String, RigBone> {
        // Sizes and joint points come from R15Skeleton, the figure the server checks poses against (floor, limits).
        val segments = R15Skeleton.SEGMENTS.map { seg ->
            arrayOf<Any?>(
                seg.joint, seg.parent, seg.pivot.x, seg.pivot.y, seg.pivot.z,
                seg.size.x, seg.size.y, seg.size.z, seg.center.x, seg.center.y, seg.center.z
            )
        }.toTypedArray()
        val pivots: dynamic = buildRigJs(scene, segments, RIG_ROOT_Y)
        val result = mutableMapOf<String, RigBone>()
        val names = js("Object.keys(pivots)").unsafeCast<Array<String>>()
        for (name in names) {
            val g = pivots[name]
            result[name] = RigBone(
                mesh = g,
                defaultPosition = g.position.clone(),
                defaultQuaternion = g.quaternion.clone(),
                currentPosition = g.position.clone(),
                currentQuaternion = g.quaternion.clone()
            )
        }
        return result
    }

    /** Puts the setup's weapons into the manikin's hands (the grip point of each hand, like RightGripAttachment). */
    fun attachWeapons(rig: Map<String, RigBone>, setup: AnimationSetup, correction: Map<String, dynamic> = emptyMap()) {
        if (setup.mode != CombatMode.ONE_WEAPON && setup.mode != CombatMode.TWO_WEAPONS) return
        fun hold(handJoint: String, kind: WeaponKind, modelUrl: String?) {
            val hand = rig[handJoint]?.mesh ?: return
            val holder = js("new window.THREE.Group()")
            holder.position.set(AnimationWeaponsGrip.x, AnimationWeaponsGrip.y, AnimationWeaponsGrip.z)
            // A character's hand bone keeps its own rest direction: the holder turns back to the R15 hand frame.
            // Not ?.let: on a dynamic value Kotlin/JS calls a JS method named "let".
            val turn: dynamic = correction[handJoint]
            if (turn != null) {
                holder.quaternion.copy(turn)
                holder.position.applyQuaternion(turn)
                // An armature in centimetres scales everything under it: the weapon keeps its size in studs.
                val scale = (hand.getWorldScale(js("new window.THREE.Vector3()")).x as Double).takeIf { it > 1e-6 } ?: 1.0
                holder.scale.setScalar(1.0 / scale)
                holder.position.multiplyScalar(1.0 / scale)
            }
            hand.add(holder)
            holder.add(buildWeaponFunction(kind.name))
            if (modelUrl != null && js("typeof window.THREE.GLTFLoader !== 'undefined'") as Boolean) {
                js("new window.THREE.GLTFLoader()").load(BackendEndpoint.resolveAssetUrl(modelUrl), { gltf: dynamic ->
                    sceneCall("weapon model") {
                        val loaded = gltf.scene ?: gltf.scenes?.get(0)
                        if (loaded != null) {
                            // The built-in stand-in goes once the user's model is ready to hold, not before.
                            val held = holdModelFunction(loaded, weaponLength(kind))
                            while ((holder.children.length as Int) > 0) holder.remove(holder.children[0])
                            holder.add(held)
                        }
                    }
                })
            }
        }
        // A single weapon may be in the left hand (the clip is mirrored for it); two weapons: one in each hand.
        hold(setup.mainHandJoint, setup.weapon, setup.weaponModelUrl)
        if (setup.mode == CombatMode.TWO_WEAPONS) hold("LeftHand", setup.offhand, setup.offhandModelUrl)
    }

    /**
     * Loads a rigged character and plays the animation on its bones: they are named like the R15 joints and carry no
     * rest rotation, so the manikin's pose code drives them unchanged. The file stands on Y = 0 in studs.
     */
    fun loadCharacter(scene: dynamic, url: String, manikin: Map<String, RigBone>, setup: AnimationSetup) {
        if (!(js("typeof window.THREE.GLTFLoader !== 'undefined'") as Boolean)) return
        js("new window.THREE.GLTFLoader()").load(BackendEndpoint.resolveAssetUrl(url), { gltf: dynamic ->
            sceneCall("character") {
                // The viewer rebuilds its scene (first layout, full screen): a character loaded for a scene that is
                // gone must not become the rig the animation drives.
                if (threeScene !== scene) return@sceneCall
                val character = gltf.scene ?: gltf.scenes?.get(0) ?: return@sceneCall
                val found = mutableMapOf<R15Skeleton.Segment, dynamic>()
                for (seg in R15Skeleton.SEGMENTS) {
                    val bone: dynamic = character.getObjectByName(seg.joint)
                    if (bone != null && bone != undefined) found[seg] = bone
                }
                if (found.size < R15Skeleton.SEGMENTS.size) return@sceneCall
                character.position.y = groundY
                scene.add(character)
                character.updateMatrixWorld(true)
                // Per bone: the turn from the R15 limb direction to this character's (to its child joint, or for
                // hands, feet and the head the bone before), taken back by toR15 every frame.
                val bones = mutableMapOf<String, CharacterBone>()
                val handTurn = mutableMapOf<String, dynamic>()
                for ((seg, bone) in found) {
                    val child = R15Skeleton.SEGMENTS.firstOrNull { it.parent == seg.joint }
                    val toR15: dynamic = if (seg.parent == null) js("new window.THREE.Quaternion()") else {
                        val from: dynamic = if (child != null) bone else found.getValue(R15Skeleton.BY_JOINT.getValue(seg.parent!!))
                        val to: dynamic = if (child != null) found.getValue(child) else bone
                        val actual: dynamic = to.getWorldPosition(js("new window.THREE.Vector3()")).sub(from.getWorldPosition(js("new window.THREE.Vector3()")))
                        // The R15 limb points straight up (torso, head) or down (arms, legs), like in the plugin; the
                        // joint points cannot be used, the shoulder sits on the torso's edge beside the arm.
                        val up = seg.joint == "UpperTorso" || seg.joint == "Head"
                        val r15: dynamic = js("new window.THREE.Vector3()").set(0.0, if (up) 1.0 else -1.0, 0.0)
                        if ((actual.length() as Double) < 1e-6) js("new window.THREE.Quaternion()")
                        else js("new window.THREE.Quaternion()").setFromUnitVectors(r15.normalize(), actual.normalize()).invert()
                    }
                    val restWorld: dynamic = bone.getWorldQuaternion(js("new window.THREE.Quaternion()"))
                    bones[seg.joint] = CharacterBone(bone, restWorld, toR15, bone.getWorldPosition(js("new window.THREE.Vector3()")))
                    // A held weapon sits in the R15 hand frame: restWorld⁻¹ · toR15⁻¹ in the bone's own frame.
                    handTurn[seg.joint] = restWorld.clone().invert().multiply(toR15.clone().invert())
                }
                // Finger bones (template skeleton): rest direction along the hand, like the R15 hand.
                for (finger in R15Fingers.BONES) {
                    val bone: dynamic = character.getObjectByName(finger.name)
                    if (bone == null || bone == undefined) continue
                    val next: dynamic = R15Fingers.BY_NAME["${finger.side}${finger.finger}${finger.index + 1}"]?.let { character.getObjectByName(it.name) }
                    val from: dynamic = if (next != null && next != undefined) bone else bone.parent
                    val to: dynamic = if (next != null && next != undefined) next else bone
                    val actual: dynamic = to.getWorldPosition(js("new window.THREE.Vector3()")).sub(from.getWorldPosition(js("new window.THREE.Vector3()")))
                    val r15: dynamic = js("new window.THREE.Vector3()").set(R15Fingers.REST_DIRECTION.x, R15Fingers.REST_DIRECTION.y, R15Fingers.REST_DIRECTION.z)
                    val toR15: dynamic = if ((actual.length() as Double) < 1e-6) js("new window.THREE.Quaternion()")
                        else js("new window.THREE.Quaternion()").setFromUnitVectors(r15, actual.normalize()).invert()
                    bones[finger.name] = CharacterBone(
                        bone, bone.getWorldQuaternion(js("new window.THREE.Quaternion()")), toR15, bone.getWorldPosition(js("new window.THREE.Vector3()"))
                    )
                }
                manikin["LowerTorso"]?.mesh?.parent?.visible = false
                val handRig = found.entries.associate { (seg, bone) ->
                    seg.joint to RigBone(bone, bone.position.clone(), bone.quaternion.clone(), bone.position.clone(), bone.quaternion.clone())
                }
                attachWeapons(handRig, setup, handTurn)
                characterRig = bones
            }
        })
    }

    fun initThree(canvas: HTMLCanvasElement) {
        try {
            val three = js("window.THREE")
            if (three == undefined || three == null) {
                return
            }

            val parent = canvas.parentElement
            val width = parent?.clientWidth?.toDouble()?.takeIf { it > 50 }
                ?: canvas.clientWidth.toDouble().takeIf { it > 50 }
                ?: 600.0
            val height = parent?.clientHeight?.toDouble()?.takeIf { it > 50 }
                ?: if (isFullscreen) window.innerHeight.toDouble() else viewerHeightPx.toDouble()

            val scene: dynamic = js("new window.THREE.Scene()")
            val bg = js("new window.THREE.Color(0x0f0f18)")
            scene.background = bg
            val camera: dynamic = js("new window.THREE.PerspectiveCamera(45, width / height, 0.05, 1000)")
            val renderer: dynamic = js("new window.THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, preserveDrawingBuffer: true })")
            renderer.setSize(width, height, false)
            renderer.setPixelRatio(window.devicePixelRatio)
            renderer.setClearColor(0x0f0f18, 1)
            renderer.outputColorSpace = js("window.THREE.SRGBColorSpace || 'srgb'")
            // three r128 reads outputEncoding: without sRGB output, textures (decoded from sRGB) render too dark.
            renderer.outputEncoding = js("window.THREE.sRGBEncoding")
            MeshSceneJs.setBackground(scene, renderer, view.background.color)

            updateLighting(scene, lightingPreset)

            // Grid helper
            val gridHelper = js("new window.THREE.GridHelper(16, 32, 0xa855f7, 0x2e2e42)")
            gridHelper.position.y = groundY
            gridHelper.visible = showGrid && view.background != MeshBackground.BASEPLATE
            scene.add(gridHelper)
            threeGrid = gridHelper

            // Axes helper
            val axesHelper = js("new window.THREE.AxesHelper(1.8)")
            axesHelper.position.set(-2.5, -1.6, -2.5)
            axesHelper.visible = showAxes
            scene.add(axesHelper)
            threeAxes = axesHelper

            val proceduralGroup: dynamic = if (isAnimationMode) {
                val rig = generateR15Rig(scene)
                rigMap = rig
                characterRig = null
                val setup = AnimationSetup.fromMetadata(animationMetadata)
                sceneCall("weapons") { attachWeapons(rig, setup) }
                // An own character (a 3D work with a template skeleton) takes the manikin's place once loaded.
                setup.characterUrl?.let { url -> sceneCall("character") { loadCharacter(scene, url, rig, setup) } }
                try {
                    applyPoseToRig(0.0, rig, animKeyframes)
                } catch (e: Throwable) {
                    js("console.error('[MeshViewer.anim] initial applyPose err', e)")
                }
                null
            } else {
                val group = buildProceduralMesh(prompt)
                if (group != null) {
                    scene.add(group)

                    // Auto-center and frame
                    js("""
                        (function(g, cam) {
                            var THREE = window.THREE;
                            var box = new THREE.Box3();
                            box.setFromObject(g);
                            var center = box.getCenter(new THREE.Vector3());
                            var size = box.getSize(new THREE.Vector3());
                            var maxDim = Math.max(size.x, size.y, size.z, 0.5);
                            g.position.sub(center);
                        })(group, camera);
                    """)

                    applyShadingModeToMesh(group, shadingMode)
                    threeMesh = group
                    modelStats = calculateStatsForObject(group)
                }
                group
            }

            threeScene = scene
            threeRenderer = renderer
            threeCamera = camera
            isInitialized = true

            // Set up ResizeObserver to smoothly resize canvas
            js("""
                (function(canv, ren, cam) {
                    if (window.ResizeObserver && canv.parentElement) {
                        var ro = new window.ResizeObserver(function(entries) {
                            for (var entry of entries) {
                                var w = Math.floor(entry.contentRect.width);
                                var h = Math.floor(entry.contentRect.height);
                                if (w > 20 && h > 20 && ren && cam) {
                                    ren.setSize(w, h, false);
                                    cam.aspect = w / h;
                                    cam.updateProjectionMatrix();
                                }
                            }
                        });
                        ro.observe(canv.parentElement);
                        canv.__resizeObserver = ro;
                    }
                })(canvas, renderer, camera);
            """)

            fun updateCameraPosition() {
                // The field of view is vertical: on a narrow (phone) viewer step back so the sides still fit.
                val d = zoomDist * maxOf(1.0, 1.0 / ((camera.aspect as? Double) ?: 1.0))
                val cy = sin(rotX) * d
                val hd = cos(rotX) * d
                val cx = sin(rotY) * hd
                val cz = cos(rotY) * hd

                camera.position.set(targetX + cx, targetY + cy, targetZ + cz)
                camera.lookAt(targetX, targetY, targetZ)
            }

            updateCameraPosition()
            renderer.render(scene, camera)

            fun localLoop() {
                try {
                    val cam = threeCamera
                    val sc = threeScene
                    val ren = threeRenderer
                    if (cam != null && sc != null && ren != null) {
                        val now = js("performance.now()").unsafeCast<Double>()
                        val delta = if (lastFrameNs > 0.0) (now - lastFrameNs) / 1000.0 else 0.0
                        lastFrameNs = now

                        val rig = rigMap
                        // Read through the state on every frame: this loop outlives the composition that started it,
                        // and a value captured then (before the clip was parsed) kept the figure still for good.
                        val clip = animSequence
                        val keyframesNow = clip?.keyframes ?: emptyList()
                        if (isAnimationMode && rig != null) {
                            val dur = clip?.duration?.toDouble() ?: 1.0
                            if (isPlaying && dur > 0.01) {
                                currentTime += speedFactor * delta
                                currentTime = wrapTime(currentTime, dur, loopEnabled)
                                if (!loopEnabled && currentTime >= dur - 0.0001) {
                                    currentTime = dur
                                    isPlaying = false
                                }
                            }
                            try {
                                val character = characterRig
                                if (character != null) applyPoseToCharacter(currentTime, character, keyframesNow)
                                else applyPoseToRig(currentTime, rig, keyframesNow)
                            } catch (e: Throwable) {
                                js("console.error('[MeshViewer.anim] applyPose err', e)")
                            }
                        }

                        if (videoSpin) {
                            // One full turn over the video's length, whatever the frame rate.
                            rotY += delta * 2 * kotlin.math.PI * 1000.0 / VIDEO_MS
                        } else if (autoRotate && !isDraggingOrbit && !isDraggingPan) {
                            rotY += 0.008
                        }

                        updateCameraPosition()
                        ren.render(sc, cam)
                    }
                } catch (_: Throwable) {}
                animFrameId = window.requestAnimationFrame { localLoop() }
            }

            if (animFrameId != 0) try { window.cancelAnimationFrame(animFrameId) } catch (_: Throwable) {}
            animFrameId = window.requestAnimationFrame { localLoop() }

            // Every new scene loads the model again (the browser has it cached): a scene rebuilt after the
            // first load (fullscreen, a second init) would otherwise keep only the placeholder.
            if (!isAnimationMode && !modelUrl.isNullOrBlank()) {
                try {
                    val loader = js("new window.THREE.GLTFLoader()")
                    loader.load(
                        modelUrl,
                        load@{ gltf: dynamic ->
                            try {
                                val loadedScene = gltf.scene ?: gltf.scenes?.get(0)
                                if (loadedScene != null) {
                                    // Measured before scaling: the file is in studs.
                                    val measured = calculateStatsForObject(loadedScene)
                                    val box = js("""
                                        (function(ls) {
                                            var b = new window.THREE.Box3();
                                            b.setFromObject(ls);
                                            return b;
                                        })(loadedScene)
                                    """)
                                    val size = box.getSize(js("new window.THREE.Vector3()"))
                                    val center = box.getCenter(js("new window.THREE.Vector3()"))
                                    val maxDim = js("Math.max(size.x, size.y, size.z, 0.5)") as Double
                                    val scaleFactor = 3.2 / (if (maxDim > 0) maxDim else 1.0)
                                    loadedScene.scale.setScalar(scaleFactor)
                                    // The offset is in scaled units too, so the model's centre lands on the origin.
                                    loadedScene.position.copy(center).multiplyScalar(-scaleFactor)
                                    modelScale = scaleFactor
                                    groundY = -(size.y as Double) / 2 * scaleFactor
                                    modelRightX = (size.x as Double) / 2 * scaleFactor
                                    val grid = threeGrid
                                    if (grid != null) grid.position.y = groundY
                                    if (proceduralGroup != null) {
                                        try { scene.remove(proceduralGroup) } catch (_: Throwable) {}
                                    }
                                    // A load that finishes after the scene was rebuilt belongs to the old scene.
                                    if (threeScene !== scene) return@load
                                    scene.add(loadedScene)
                                    applyShadingModeToMesh(loadedScene, shadingMode)
                                    threeMesh = loadedScene
                                    modelLoaded = true
                                    modelStats = measured
                                    applyCameraPreset(CameraViewPreset.RESET)
                                }
                            } catch (e: Throwable) {
                                js("console.error('[MeshViewer] GLTF parse err', e);")
                            }
                        },
                        { _: dynamic -> },
                        { err: dynamic ->
                            js("console.error('[MeshViewer] GLTF load error', err);")
                        }
                    )
                } catch (e: Throwable) {
                    js("console.error('[MeshViewer] GLTFLoader create err', e);")
                }
            }
        } catch (e: Throwable) {
            js("console.error('[MeshViewer] initThree FATAL', e);")
        }
    }

    LaunchedEffect(canvasRef, isFullscreen, prompt, modelUrl) {
        val canvas = canvasRef ?: return@LaunchedEffect
        ensureThreeJsLoaded(onLoaded = {
            initThree(canvas)
        })
    }

    LaunchedEffect(shadingMode, threeMesh, isInitialized) {
        if (!isInitialized) return@LaunchedEffect
        val mesh = threeMesh
        if (mesh != null) {
            applyShadingModeToMesh(mesh, shadingMode)
        }
    }

    LaunchedEffect(lightingPreset, threeScene, isInitialized) {
        if (!isInitialized) return@LaunchedEffect
        val sc = threeScene
        if (sc != null) {
            updateLighting(sc, lightingPreset)
        }
    }

    LaunchedEffect(showGrid, threeGrid, view.background) {
        val grid = threeGrid
        if (grid != null) {
            grid.visible = showGrid && view.background != MeshBackground.BASEPLATE
        }
    }

    LaunchedEffect(view.background, threeScene, groundY, modelScale) {
        val sc = threeScene ?: return@LaunchedEffect
        val ren = threeRenderer ?: return@LaunchedEffect
        sceneCall("background") {
            MeshSceneJs.setBackground(sc, ren, view.background.color)
            MeshSceneJs.baseplate(sc, groundY, modelScale, view.background == MeshBackground.BASEPLATE)
        }
    }

    LaunchedEffect(view.showReference, threeScene, groundY, modelScale, modelRightX) {
        val sc = threeScene ?: return@LaunchedEffect
        if (isAnimationMode) return@LaunchedEffect
        // An R15 avatar a stud and a half from the model on -X: from the default camera it stands behind and to
        // the side, so it does not cover the model.
        val avatarX = -(modelRightX + (1.5 + AVATAR_HALF_WIDTH) * modelScale)
        sceneCall("reference") { MeshSceneJs.referenceRig(sc, modelScale, avatarX, groundY, view.showReference) }
        if (view.showReference) {
            // Frame the model and the avatar together.
            val avatarHeight = dev.danko.roblox_craft_ai.shared.model.RobloxMeshLimits.R15_HEIGHT_STUDS * modelScale
            val left = avatarX - AVATAR_HALF_WIDTH * modelScale
            val right = modelRightX
            val top = maxOf(groundY + avatarHeight, -groundY)
            targetX = (left + right) / 2
            targetY = (groundY + top) / 2
            zoomDist = (maxOf(right - left, top - groundY) * 1.7).coerceIn(defaultZoom, ZOOM_MAX)
        } else {
            targetX = 0.0
            targetY = 0.0
            zoomDist = defaultZoom
        }
    }

    LaunchedEffect(view.showBounds, threeMesh, modelStats) {
        val sc = threeScene ?: return@LaunchedEffect
        val size = modelStats.sizeStuds
        val labels = if (size.size == 3) size.map { s.meshInspector.studs(it) }.toTypedArray() else arrayOf("", "", "")
        sceneCall("bounds") { MeshSceneJs.bounds(sc, threeMesh, view.showBounds && threeMesh != null, labels) }
    }

    LaunchedEffect(view.videoRequests) {
        if (view.videoRequests == 0) return@LaunchedEffect
        val canvas = canvasRef ?: return@LaunchedEffect
        val name = slugify(prompt.take(40))
        val started = MeshSceneJs.record(canvas, VIDEO_MS) { blob: dynamic, ext: String ->
            videoSpin = false
            view.recording = false
            val url = URL.createObjectURL(blob.unsafeCast<Blob>())
            val a = document.createElement("a")
            a.setAttribute("href", url)
            a.setAttribute("download", "${name}_spin.$ext")
            document.body?.appendChild(a)
            a.asDynamic().click()
            document.body?.removeChild(a)
            window.setTimeout({ URL.revokeObjectURL(url) }, 2000)
        } as Boolean
        if (started) {
            view.recording = true
            videoSpin = true
        } else {
            try { ToastManager.warning(s.meshInspector.videoUnsupported) } catch (_: Throwable) {}
        }
    }

    LaunchedEffect(view.showRelief, threeMesh) {
        val mesh = threeMesh ?: return@LaunchedEffect
        sceneCall("relief") { MeshSceneJs.relief(mesh, view.showRelief) }
    }

    LaunchedEffect(view.screenshotRequests) {
        if (view.screenshotRequests == 0) return@LaunchedEffect
        val ren = threeRenderer ?: return@LaunchedEffect
        val sc = threeScene ?: return@LaunchedEffect
        val cam = threeCamera ?: return@LaunchedEffect
        val url = MeshSceneJs.snapshot(ren, sc, cam, view.screenshotTransparent) as String
        val name = "${slugify(prompt.take(40))}${if (view.screenshotTransparent) "_transparent" else ""}.png"
        val a = document.createElement("a")
        a.setAttribute("href", url)
        a.setAttribute("download", name)
        document.body?.appendChild(a)
        a.asDynamic().click()
        document.body?.removeChild(a)
        try { ToastManager.success(s.meshViewer.screenshotSaved(name), title = s.meshViewer.screenshotTitle) } catch (_: Throwable) {}
    }

    LaunchedEffect(showAxes, threeAxes) {
        val axes = threeAxes
        if (axes != null) {
            axes.visible = showAxes
        }
    }

    // Global drag listener for smooth height resizing
    DisposableEffect(isResizingHeight) {
        if (isResizingHeight) {
            val onMove: (dynamic) -> Unit = { e ->
                val curY = e.clientY.unsafeCast<Double>()
                val dy = curY - startDragY
                val newH = (startHeight + dy.toInt()).coerceIn(260, 950)
                viewerHeightPx = newH
            }
            val onUp: (dynamic) -> Unit = {
                isResizingHeight = false
            }
            window.addEventListener("mousemove", onMove)
            window.addEventListener("mouseup", onUp)
            onDispose {
                window.removeEventListener("mousemove", onMove)
                window.removeEventListener("mouseup", onUp)
            }
        } else {
            onDispose {}
        }
    }

    DisposableEffect(Unit) {
        onDispose {
            if (animFrameId != 0) {
                window.cancelAnimationFrame(animFrameId)
            }
            try {
                threeRenderer?.dispose()
            } catch (_: Throwable) {}
        }
    }

    fun onRbxmExportClicked() {
        val seq = animSequence
        val name = seq?.name ?: "animation"
        val filename = "${slugify(name)}.keyframes.json"
        val jsonForExport: String = if (seq != null && seq.keyframes.isNotEmpty()) {
            val kfStr = buildString {
                seq.keyframes.forEachIndexed { kfIdx, kf ->
                    if (kfIdx > 0) append(",")
                    append("{")
                    append("\"time\":").append(kf.time).append(",")
                    append("\"easingStyle\":\"").append(kf.easingStyle.name).append("\",")
                    append("\"easingDirection\":\"").append(kf.easingDirection.name).append("\",")
                    append("\"poses\":{")
                    val entries = kf.poses.entries.toList()
                    entries.forEachIndexed { pIdx, (bn, pose) ->
                        if (pIdx > 0) append(",")
                        val px = pose.position.first
                        val py = pose.position.second
                        val pz = pose.position.third
                        val rx = pose.rotation.first
                        val ry = pose.rotation.second
                        val rz = pose.rotation.third
                        append("\"").append(bn).append("\":")
                        append("{\"pos\":[").append(px).append(",").append(py).append(",").append(pz).append("],")
                        append("\"rot\":[").append(rx).append(",").append(ry).append(",").append(rz).append("]}")
                    }
                    append("}}")
                }
            }
            val nm = if (seq.name != null) "\"${seq.name}\"" else "null"
            "{\"name\":$nm,\"duration\":${seq.duration},\"looped\":${seq.looped},\"keyframes\":[$kfStr]}"
        } else {
            animationKeyframesJson ?: "{}"
        }
        downloadBlob(filename, jsonForExport, mime = "application/json")
    }

    val containerModifier = if (isFullscreen) {
        Modifier
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .width(100.vw)
            .height(100.vh)
            .zIndex(99999)
            .backgroundColor(Color.rgb(0x0a0a10))
    } else {
        MeshViewerContainerStyle.toModifier().then(modifier)
    }

    Column(modifier = containerModifier) {
        // Main 3D Viewport Box
        Box(
            Modifier
                .fillMaxWidth()
                // In the animation workspace the view takes what the timeline leaves.
                .then(if (isFullscreen && isAnimationMode) Modifier.flexGrow(1).minHeight(0.px) else Modifier.height(if (isFullscreen) 100.vh else viewerHeightPx.px))
                .cursor(if (isDraggingOrbit || isDraggingPan) Cursor.Grabbing else Cursor.Grab)
                .userSelect(UserSelect.None)
                .position(Position.Relative)
                .onContextMenu { it.preventDefault() }
                .onMouseDown { evt ->
                    val nativeEvt = evt.nativeEvent.asDynamic()
                    val button = nativeEvt.button as? Int ?: 0
                    val isShift = nativeEvt.shiftKey == true
                    lastMouseX = evt.clientX.toDouble()
                    lastMouseY = evt.clientY.toDouble()

                    if (button == 2 || (button == 0 && isShift) || button == 1) {
                        isDraggingPan = true
                        isDraggingOrbit = false
                    } else if (button == 0) {
                        isDraggingOrbit = true
                        isDraggingPan = false
                    }
                }
                .onMouseMove { evt ->
                    val curX = evt.clientX.toDouble()
                    val curY = evt.clientY.toDouble()
                    val dx = curX - lastMouseX
                    val dy = curY - lastMouseY

                    if (isDraggingOrbit) {
                        rotY += dx * DRAG_SENSITIVITY
                        rotX = (rotX + dy * DRAG_SENSITIVITY).coerceIn(ROT_X_MIN, ROT_X_MAX)
                    } else if (isDraggingPan) {
                        val panFactor = zoomDist * PAN_SENSITIVITY
                        val rightX = cos(rotY)
                        val rightZ = -sin(rotY)
                        val upX = -sin(rotX) * sin(rotY)
                        val upY = cos(rotX)
                        val upZ = -sin(rotX) * cos(rotY)

                        targetX -= (rightX * dx + upX * -dy) * panFactor
                        targetY -= (upY * -dy) * panFactor
                        targetZ -= (rightZ * dx + upZ * -dy) * panFactor
                    }
                    lastMouseX = curX
                    lastMouseY = curY
                }
                .onMouseUp {
                    isDraggingOrbit = false
                    isDraggingPan = false
                }
                .onMouseLeave {
                    isDraggingOrbit = false
                    isDraggingPan = false
                }
                .onWheel { evt ->
                    // The browser event, not Compose's wrapper: the wrapper has neither preventDefault nor deltaY,
                    // so the wheel threw, never zoomed and scrolled the page instead.
                    val wheel = evt.nativeEvent.asDynamic()
                    wheel.preventDefault()
                    val delta = (wheel.deltaY as? Number)?.toDouble() ?: 0.0
                    zoomDist = (zoomDist * (1.0 + delta * ZOOM_WHEEL_FACTOR)).coerceIn(ZOOM_MIN, ZOOM_MAX)
                }
                .onDoubleClick {
                    applyCameraPreset(CameraViewPreset.RESET)
                }
        ) {
            Canvas(
                attrs = {
                    style {
                        property("width", "100%")
                        property("height", "100%")
                        property("display", "block")
                        property("outline", "none")
                    }
                    ref { elem ->
                        canvasRef = elem
                        onDispose { canvasRef = null }
                    }
                }
            )

            // TOP CONTROLS (style A): material and light as two compact selects, actions as icons.
            if (!compact) Row(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(10.px)
                    .left(10.px)
                    .right(10.px)
                    .zIndex(10)
                    .gap(6.px)
                    .flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.CenterVertically
            ) {
                ViewerSelect(
                    label = s.meshViewer.shadingTextured,
                    options = MeshShadingMode.values().map { it.name to it.localizedTitle(s.meshViewer) },
                    selectedValue = shadingMode.name,
                    onSelect = { name -> shadingMode = MeshShadingMode.valueOf(name) }
                )
                ViewerSelect(
                    label = s.meshViewer.lightStudio,
                    options = MeshLightingPreset.values().map { it.name to it.localizedTitle(s.meshViewer) },
                    selectedValue = lightingPreset.name,
                    onSelect = { name -> lightingPreset = MeshLightingPreset.valueOf(name) }
                )
                Spacer()
                ViewerIconButton(
                    icon = AppIcon.ROTATE,
                    label = if (autoRotate) s.meshViewer.pause else s.meshViewer.spin,
                    pressed = autoRotate
                ) { autoRotate = !autoRotate }
                ViewerIconButton(icon = AppIcon.MINUS, label = s.animationSetup.zoomOut) {
                    zoomDist = (zoomDist * 1.25).coerceIn(ZOOM_MIN, ZOOM_MAX)
                }
                ViewerIconButton(icon = AppIcon.PLUS, label = s.animationSetup.zoomIn) {
                    zoomDist = (zoomDist / 1.25).coerceIn(ZOOM_MIN, ZOOM_MAX)
                }
                ViewerIconButton(icon = AppIcon.CAMERA, label = s.meshViewer.screenshot) {
                    takeMeshScreenshot(threeRenderer, threeScene, threeCamera, "roblox_model_view.png")
                }
                ViewerIconButton(
                    icon = if (isFullscreen) AppIcon.CLOSE else AppIcon.MAXIMIZE,
                    label = when {
                        isFullscreen && isAnimationMode -> s.animationSetup.closeWorkspace
                        isFullscreen -> s.meshViewer.exitFullscreen
                        isAnimationMode -> s.animationSetup.openWorkspace
                        else -> s.meshViewer.fullscreen
                    },
                    pressed = isFullscreen
                ) {
                    isFullscreen = !isFullscreen
                    window.setTimeout({
                        val canvas = canvasRef
                        if (canvas != null) initThree(canvas)
                    }, 50)
                }
            }

            // BOTTOM: camera angles on the left, model stats on the right.
            if (!compact) Row(
                modifier = Modifier
                    .position(Position.Absolute)
                    .bottom(10.px)
                    .left(10.px)
                    .right(10.px)
                    .gap(8.px)
                    .flexWrap(FlexWrap.Wrap)
                    .zIndex(10),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    Modifier
                        .padding(3.px)
                        .gap(2.px)
                        .borderRadius(10.px)
                        .backgroundColor(palette.surface.toRgb().copy(alpha = 235))
                        .border(1.px, LineStyle.Solid, palette.borderStrong),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    CameraViewPreset.values().forEach { preset ->
                        org.jetbrains.compose.web.dom.Button(
                            Modifier
                                .height(30.px)
                                .padding(leftRight = 10.px)
                                .borderRadius(7.px)
                                .border(0.px, LineStyle.None, Colors.Transparent)
                                .backgroundColor(Colors.Transparent)
                                .color(palette.textSecondary)
                                .fontSize(12.px)
                                .fontWeight(FontWeight.SemiBold)
                                .cursor(Cursor.Pointer)
                                .styleModifier { property("font-family", "inherit") }
                                .onClick { applyCameraPreset(preset) }
                                .toAttrs { attr("type", "button") }
                        ) { SpanText(preset.localizedLabel(s.meshViewer)) }
                    }
                }
                Spacer()
                SpanText(
                    s.meshViewer.stats(modelStats.triangles.toString(), modelStats.vertices.toString(), modelStats.dimensionsStuds).removePrefix("📊 "),
                    Modifier
                        .fontSize(12.px)
                        .color(palette.textSecondary)
                        .padding(topBottom = 4.px, leftRight = 8.px)
                        .borderRadius(8.px)
                        .backgroundColor(palette.surface.toRgb().copy(alpha = 220))
                        .title(s.meshViewer.controlsHint)
                )
            }

            // The clip and joint readout also fits the wide animation panel.
            if (isAnimationMode && (isFullscreen || showTimeline)) {
                AnimationSidePanel(animSequence, currentTime, Modifier.position(Position.Absolute).top(60.px).right(12.px).zIndex(10))
            }
            if (isFullscreen && isAnimationMode) {
                SpanText(
                    s.animationSetup.cameraHint,
                    Modifier.position(Position.Absolute).bottom(54.px).left(12.px).zIndex(10).fontSize(12.px)
                        .color(palette.textSecondary).padding(topBottom = 4.px, leftRight = 8.px).borderRadius(8.px)
                        .backgroundColor(palette.surface.toRgb().copy(alpha = 200))
                )
            }
        }

        // A slim grab bar to change the viewer height by dragging.
        if (!isFullscreen && !compact) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(14.px)
                    .cursor(Cursor.RowResize)
                    .userSelect(UserSelect.None)
                    .title(s.meshViewer.resizeHint(viewerHeightPx))
                    .onMouseDown { evt ->
                        evt.preventDefault()
                        isResizingHeight = true
                        startDragY = evt.clientY.toDouble()
                        startHeight = viewerHeightPx
                    },
                contentAlignment = Alignment.Center
            ) {
                Box(Modifier.width(48.px).height(4.px).borderRadius(999.px).backgroundColor(palette.borderStrong))
            }
        }

        // The animation workspace keeps the timeline under the view; the panel shows it when asked to.
        if (isAnimationMode && (showTimeline || isFullscreen)) {
            val isPlayingState = remember { object : MutableState<Boolean> {
                override var value: Boolean
                    get() = isPlaying
                    set(v) { isPlaying = v }
                override fun component1(): Boolean = isPlaying
                override fun component2(): (Boolean) -> Unit = { isPlaying = it }
            } }

            val currentTimeState = remember { object : MutableState<Double> {
                override var value: Double
                    get() = currentTime
                    set(v) { currentTime = v }
                override fun component1(): Double = currentTime
                override fun component2(): (Double) -> Unit = { currentTime = it }
            } }

            val speedState = remember { object : MutableState<Double> {
                override var value: Double
                    get() = speedFactor
                    set(v) { speedFactor = v }
                override fun component1(): Double = speedFactor
                override fun component2(): (Double) -> Unit = { speedFactor = it }
            } }

            val loopState = remember { object : MutableState<Boolean> {
                override var value: Boolean
                    get() = loopEnabled
                    set(v) { loopEnabled = v }
                override fun component1(): Boolean = loopEnabled
                override fun component2(): (Boolean) -> Unit = { loopEnabled = it }
            } }

            AnimationTimeline(
                duration = duration,
                keyframes = animKeyframes,
                isPlayingState = isPlayingState,
                currentTimeState = currentTimeState,
                speedState = speedState,
                loopState = loopState,
                onExportRbxm = { onRbxmExportClicked() }
            )
        }
    }
}

@Composable
private fun ViewerSelect(label: String, options: List<Pair<String, String>>, selectedValue: String, onSelect: (String) -> Unit) {
    val palette = LocalAppTheme.current.colors
    org.jetbrains.compose.web.dom.Select(
        attrs = Modifier
            .height(36.px)
            .padding(leftRight = 10.px)
            .borderRadius(9.px)
            .border(1.px, LineStyle.Solid, palette.borderStrong)
            .backgroundColor(palette.surface.toRgb().copy(alpha = 235))
            .color(palette.textPrimary)
            .fontSize(13.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .styleModifier { property("font-family", "inherit") }
            .toAttrs {
                attr("aria-label", label)
                onChange { event -> event.value?.let(onSelect) }
            }
    ) {
        options.forEach { (value, title) ->
            org.jetbrains.compose.web.dom.Option(value, attrs = { if (value == selectedValue) attr("selected", "selected") }) {
                org.jetbrains.compose.web.dom.Text(title)
            }
        }
    }
}

@Composable
private fun ViewerIconButton(icon: AppIcon, label: String, pressed: Boolean = false, onClick: () -> Unit) {
    val palette = LocalAppTheme.current.colors
    org.jetbrains.compose.web.dom.Button(
        Modifier
            .size(36.px)
            .padding(0.px)
            .borderRadius(9.px)
            .border(1.px, LineStyle.Solid, palette.borderStrong)
            .backgroundColor(if (pressed) palette.accent.toRgb().copy(alpha = 64) else palette.surface.toRgb().copy(alpha = 235))
            .color(palette.textPrimary)
            .cursor(Cursor.Pointer)
            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
            .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
            .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center)
            .onClick { onClick() }
            .toAttrs {
                attr("type", "button")
                attr("aria-label", label)
                attr("title", label)
                attr("aria-pressed", pressed.toString())
            }
    ) { Icon(icon, size = 16) }
}
